import { styled } from '@mui/material/styles'
import logo_swissanalytica from 'assets/logo_sa_minimal.svg'
import wear_app from './assets/wear_app.png'
import hda_logo from './assets/hda_logo.png'
import harena_logo from './assets/harena_logo.png'

import sentinela_app from './assets/sentinela_app.png'
import samsung_health_app from './assets/samsung_health_app.png'
import samsung_health_monitor_app from './assets/samsung_health_monitor_app.png'
import samsung_wear_app from './assets/samsung_wear_app.png'
import { useLogo } from 'hooks/useLogo'

const APP_LINKS = {
  sentinelaMobile: {
    android:
      'https://play.google.com/store/apps/details?id=ch.swissanalytica.sentinela',
    logo: sentinela_app
  },
  sentinelaWatch: {
    android:
      'https://play.google.com/store/apps/details?id=ch.swissanalytica.sentinela',
    logo: sentinela_app
  },
  samsungWear: {
    android:
      'https://play.google.com/store/apps/details?id=com.samsung.android.app.watchmanager',
    logo: samsung_wear_app
  },
  samsungHealth: {
    android:
      'https://play.google.com/store/apps/details?id=com.sec.android.app.shealth',
    logo: samsung_health_app
  },
  samsungHealthMonitor: {
    android:
      'https://galaxystore.samsung.com/detail/com.samsung.android.shealthmonitor?langCd=pt',
    logo: samsung_health_monitor_app
  }
}

type SupportContact = {
  name: string
  phone?: string
  email: string
}

const Wrapper = styled('div')(({ theme }) => ({
  background: theme.palette.primarySoft.main,
  padding: theme.spacing(3)
}))

const Container = styled('div')(({ theme }) => ({
  maxWidth: '800px',
  margin: '0 auto',
  background: theme.palette.background.default,
  padding: theme.spacing(3),
  borderRadius: '16px',
  boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
  color: theme.palette.text.primary
}))

const Header = styled('header')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  marginBottom: '1.5rem',
  gap: '8px'
}))

const Title = styled('h1')(({ theme }) => ({
  fontSize: '1.8rem',
  fontWeight: 600
}))

const Section = styled('details')(({ theme }) => ({
  marginTop: '1.5rem',

  h2: {
    fontSize: '1.3rem',
    fontWeight: 600,
    marginBottom: '0.5rem'
  },

  summary: {
    fontSize: '1.3rem',
    fontWeight: 600,
    marginBottom: '0.5rem'
  },

  'ul, ol': {
    marginLeft: '1.5rem',
    lineHeight: 1.6
  },

  p: {
    marginBottom: '0.5rem'
  }
}))

const InfoBox = styled('div')(({ theme }) => ({
  background: theme.palette.background.default,
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: '8px',
  padding: '1rem',
  marginTop: '0.5rem'
}))

const Footer = styled('footer')(({ theme }) => ({
  marginTop: '2rem',
  fontSize: '20px',
  color: theme.palette.text.secondary,
  textAlign: 'center'
}))

const OrderedList = styled('ol')(({ theme }) => ({
  [theme.breakpoints.down('sm')]: {
    marginLeft: '0 !important',
    paddingInlineStart: '20px !important'
  }
}))

const List = styled('ul')(({ theme }) => ({
  [theme.breakpoints.down('sm')]: {
    marginLeft: '0 !important',
    paddingInlineStart: '20px !important'
  }
}))

const LogoContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'center',
  marginBlock: theme.spacing(2),
  gap: theme.spacing(4),
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
    gap: theme.spacing(2)
  }
}))

const Logo = styled('div')(({ theme }) => ({
  gap: theme.spacing(4),
  [theme.breakpoints.down('sm')]: {
    gap: theme.spacing(2),
    display: 'flex',
    img: {
      maxWidth: '150px',
      width: 'auto',
      maxHeight: '50px'
    }
  }
}))

export default function UserManual() {
  const logo = useLogo()
  const updatedAt = '09/10/2025'

  const support: Array<SupportContact> = [
    {
      name: 'Enfermeira Fabíola Cardoso',
      phone: '(17) 98102-5113',
      email: 'sentinela.hda@gmail.com'
    },
    {
      name: 'Equipe sentinela - swissanalytica',
      email: 'sentinela_hda@swissanalytica.ch'
    }
  ]

  return (
    <Wrapper>
      <Container>
        <LogoContainer>
          <Logo sx={{ flex: 1 }}>
            <img
              src={logo}
              alt='sentinela logo'
              style={{ maxWidth: '300px' }}
            />
          </Logo>
          <Logo>
            <img
              src={hda_logo}
              alt='hospital de amor logo'
              style={{ maxWidth: '100px' }}
            />
            <img
              src={harena_logo}
              alt='harena logo'
              style={{ maxWidth: '100px' }}
            />
          </Logo>
        </LogoContainer>
        <Header>
          <div style={{ width: '100%' }}>
            <Title>Manual do estudo clínico sentinela - Hospital de Amor</Title>
            <p>Instruções para pacientes</p>
          </div>
          <div style={{ width: '100%' }}>
            <p>
              <strong>Período de uso:</strong>
            </p>
            <p>10 dias antes da cirurgia e 30 dias após a cirurgia</p>
          </div>
        </Header>
        <Section open>
          <summary>Boas-vindas 👋</summary>
          <p>
            Obrigado por participar do estudo clínico. Este relógio inteligente
            irá monitorar seus sinais vitais para ajudar nossa equipe a
            acompanhar sua recuperação. Leia este manual com atenção e siga as
            instruções.
          </p>
        </Section>
        <Section>
          <summary>1. O que você recebeu</summary>
          <List>
            <li>1 relógio inteligente</li>
            <li>1 carregador</li>
            <li>1 cartão com QR-Code para este manual</li>
            <li>Contato da equipe de suporte</li>
          </List>
        </Section>
        <Section>
          <summary>2. Antes de começar</summary>
          <p>
            É necessário ter os seguintes aplicativos instalados para que o
            sistema funcione corretamente:
          </p>
          <OrderedList>
            <li>
              <img
                src={APP_LINKS.sentinelaMobile.logo}
                alt='sentinela app logo'
                width={20}
                style={{ verticalAlign: 'middle', marginRight: '8px' }}
              />
              <a
                href={APP_LINKS.sentinelaMobile.android}
                target='_blank'
                rel='noopener noreferrer'
              >
                <strong>sentinela para celular</strong>
              </a>{' '}
              — para visualizar e enviar os dados. <br />
            </li>
            <li>
              <img
                src={APP_LINKS.sentinelaMobile.logo}
                alt='sentinela app logo'
                width={20}
                style={{ verticalAlign: 'middle', marginRight: '8px' }}
              />
              <a
                href={APP_LINKS.sentinelaWatch.android}
                target='_blank'
                rel='noopener noreferrer'
              >
                <strong>sentinela para relógio</strong>
              </a>{' '}
              — para registrar os sinais vitais. <br />
            </li>
            <li>
              <img
                src={APP_LINKS.samsungHealth.logo}
                alt='samsung health app logo'
                width={20}
                style={{ verticalAlign: 'middle', marginRight: '8px' }}
              />
              <a
                href={APP_LINKS.samsungHealth.android}
                target='_blank'
                rel='noopener noreferrer'
              >
                <strong>Samsung Health</strong>
              </a>{' '}
              — para registro dos sinais vitais, instalado no celular. <br />
            </li>
            <li>
              <img
                src={APP_LINKS.samsungWear.logo}
                alt='samsung wear app logo'
                width={20}
                style={{ verticalAlign: 'middle', marginRight: '8px' }}
              />
              <a
                href={APP_LINKS.samsungWear.android}
                target='_blank'
                rel='noopener noreferrer'
              >
                <strong>Galaxy Wearable</strong>
              </a>{' '}
              — para conectar o relógio ao celular. <br />
            </li>
          </OrderedList>

          <p>
            <strong>Se o seu celular for Samsung:</strong> instale também o
            aplicativo{' '}
            <img
              src={APP_LINKS.samsungHealthMonitor.logo}
              alt='samsung health monitor app logo'
              width={20}
              style={{ verticalAlign: 'middle', marginRight: '8px' }}
            />
            <a
              href={APP_LINKS.samsungHealthMonitor.android}
              target='_blank'
              rel='noopener noreferrer'
            >
              <strong>Samsung Health Monitor</strong>
            </a>{' '}
            para garantir o registro completo dos dados. <br />
          </p>
          <p>Sem esses apps, o estudo não funcionará corretamente.</p>
        </Section>
        <Section>
          <summary>3. Instalando o app no celular</summary>
          <OrderedList>
            <li>Abra a loja de aplicativos (Play Store).</li>
            <li>
              Procure por <strong>sentinela</strong>.
            </li>
            <li>
              Toque em <em>Instalar</em>.
            </li>
            <li>Verifique se o ícone apareceu na tela.</li>
          </OrderedList>
          <p>
            Se tiver dificuldade, clique{' '}
            <a
              href={APP_LINKS.sentinelaMobile.android}
              target='_blank'
              rel='noopener noreferrer'
            >
              aqui
            </a>
          </p>
        </Section>
        <Section>
          <summary>4. Instalando o app no relógio</summary>
          <OrderedList>
            <li>
              Abra a lista de apps no relógio e procure o ícone do{' '}
              <strong>sentinela</strong>.
            </li>
            <li>
              Se não estiver, abra o app no celular → <em>Configurações</em> →{' '}
              <em>Instalar no relógio</em>.
            </li>
            <li>Confirme que o ícone aparece no relógio.</li>
          </OrderedList>
          <InfoBox>
            <strong>Importante:</strong> O relógio pedirá permissão para acesso
            aos sinais vitais. Aceite para garantir que os dados sejam enviados.
          </InfoBox>
        </Section>
        <Section>
          <summary>5. Primeiro acesso e sincronização</summary>
          <OrderedList>
            <li>Abra o app no celular.</li>
            <li>Digite o login e a senha fornecidos pela equipe.</li>
            <li>
              Autorize o acesso ao Bluetooth, notificações e restante das
              permissões.
            </li>
            <li>Abra o app no relógio e confirme a conexão com o celular.</li>
          </OrderedList>
          <p>
            Quando estiverem conectados, os dados começarão a aparecer no app do
            celular.
          </p>
        </Section>
        <Section>
          <summary>6. Uso diário do relógio</summary>
          <List>
            <li>
              Use o relógio <strong>o tempo todo</strong>, inclusive durante a
              noite.
            </li>
            <li>Tire o relógio apenas para tomar banho.</li>
            <li>
              <strong>Carregamento:</strong> carregue o relógio{' '}
              <strong>durante o banho ou durante as refeições.</strong>{' '}
              <strong>Não carregue durante à noite</strong>, pois é importante
              manter o relógio no braço durante o sono para que possamos coletar
              dados importantes.
            </li>
            <li>
              Use no <strong>pulso não dominante</strong> (ex.: se for destro,
              use no braço esquerdo).
            </li>
          </List>
        </Section>
        <Section>
          <summary>7. Conferindo os dados</summary>
          <p>No celular:</p>
          <OrderedList>
            <li>
              Abra o app <strong>sentinela</strong>.
            </li>
            <li>
              Verifique se os dados recentes aparecem (batimentos, sono, etc.).
            </li>
          </OrderedList>
          <p>No relógio:</p>
          <OrderedList>
            <li>
              Abra o app e verifique se está exibindo a seguinte tela:
              <br />
              <img
                src={wear_app}
                alt='Tela de registro de dados'
                width={200}
                style={{ borderRadius: '99px', marginTop: '8px' }}
              />
            </li>
          </OrderedList>
        </Section>
        <Section>
          <summary>8. Inserindo dados manualmente</summary>
          <OrderedList>
            <li>Abra o app no celular.</li>
            <li>Toque no sinal vital que deseja adicionar.</li>
            <li>Clique no botão de adicionar (+).</li>
            <li>Insira o valor (ex.: pressão, batimento).</li>
            <li>
              Toque em <em>Salvar</em>.
            </li>
          </OrderedList>
        </Section>
        <Section>
          <summary>9. Verificação rápida de funcionamento</summary>
          <List>
            <li>
              <strong>App do celular:</strong> verifique se há atualização dos
              dados no mesmo dia.
            </li>
            <li>
              <strong>App do relógio:</strong> confirme que esteja
              ativo/registrando.
            </li>
            <li>
              Se nada aparecer por mais de 24h, reinicie os dois dispositivos.
            </li>
          </List>
        </Section>
        <Section>
          <summary>10. Dicas importantes</summary>
          <List>
            <li>✅ Carregue só durante o banho ou durante as refeições.</li>
            <li>✅ Nunca carregue o relógio à noite ou durante o sono.</li>
            <li>✅ Use sempre no mesmo braço.</li>
            <li>✅ Evite impactos e água quente direta.</li>
            <li>✅ Mantenha o relógio limpo e seco.</li>
            <li>✅ Mantenha o Bluetooth do celular sempre ligado.</li>
            <li>✅ Mantenha os apps atualizados.</li>
            <li>
              ✅ Reinicie os dispositivos se os dados pararem de aparecer.
            </li>
            <li>❌ Não tente abrir ou consertar o relógio.</li>
            <li>❌ Não use o relógio para outras finalidades.</li>
            <li>❌ Não compartilhe o relógio com outras pessoas.</li>
            <li>❌ Não desinstale os apps.</li>
          </List>
        </Section>
        <Section>
          <summary>11. Problemas comuns e soluções</summary>
          <dl>
            <dt>Relógio não liga</dt>
            <dd>→ Coloque no carregador e espere alguns minutos.</dd>
            <dt>App não abre</dt>
            <dd>→ Reinicie o celular e tente novamente.</dd>
            <dt>Dados não aparecem</dt>
            <dd>
              → Verifique Bluetooth e conexão dos apps; reinicie se necessário.
            </dd>
            <dt>Problemas persistentes</dt>
            <dd>→ Entre em contato com a equipe de suporte.</dd>
          </dl>
        </Section>
        <Section>
          <summary>12. Contato da equipe</summary>
          {support.map((contact, index) => (
            <List key={'list' + index}>
              <li>
                <b>👤 {contact.name}</b>
              </li>
              {contact.phone && (
                <li>
                  📞{' '}
                  <a
                    target='_blank'
                    href={`https://api.whatsapp.com/send?phone=${contact.phone.replaceAll(/\D/g, '')}`}
                  >
                    {contact.phone}
                  </a>
                </li>
              )}
              <li>
                📧 <a href={`mailto:${contact.email}`}>{contact.email}</a>
              </li>
            </List>
          ))}
        </Section>
        <Footer>
          Obrigado por participar! Seu uso correto do relógio é fundamental para
          o sucesso do estudo e sua recuperação.
        </Footer>
        <br />
        <br />
        <LogoContainer>
          <div>Powered by</div>
          <Logo>
            <img
              src={logo_swissanalytica}
              alt='swissanalytica logo'
              style={{ maxWidth: '100px' }}
            />
          </Logo>
        </LogoContainer>
        <div
          style={{
            marginTop: '16px',
            fontSize: '12px',
            textAlign: 'center',
            color: 'inherit'
          }}
        >
          Atualizado em: {updatedAt}
        </div>
      </Container>
    </Wrapper>
  )
}
